/**
 * Scaling Up Action Items — standalone view across all meetings of a company.
 * Filters by status (open / overdue / done) and owner.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, ListChecks } from "lucide-react";

type Status = "open" | "done" | "overdue" | "cancelled";

interface Holding {
  id: string;
  name: string;
}
interface Company {
  id: string;
  name: string;
}
interface ActionItem {
  id: string;
  description: string;
  ownerUserId: string | null;
  dueDate: string | null;
  status: Status;
  meetingId: string | null;
  createdAt: string;
  completedAt: string | null;
}

const STATUS_CLASS: Record<Status, string> = {
  open: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  overdue: "bg-red-500/15 text-red-300 border-red-500/30",
  done: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-white/5 text-white/40 border-white/10",
};

function ScalingUpActionItemsInner() {
  const qc = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");

  const holdingsQuery = useQuery<{ items: Holding[] }>({ queryKey: ["/api/scaling-up/holdings"] });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });

  const itemsKey: readonly string[] = [
    "/api/scaling-up/companies",
    companyId ?? "",
    `action-items${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`,
  ];
  const itemsQuery = useQuery<{ items: ActionItem[] }>({
    queryKey: itemsKey,
    enabled: companyId !== null,
  });

  const updateMutation = useMutation({
    mutationFn: async (params: { id: string; status: Status }) =>
      apiRequest("PATCH", `/api/scaling-up/action-items/${params.id}`, { status: params.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const items = useMemo(() => itemsQuery.data?.items ?? [], [itemsQuery.data]);

  const grouped = useMemo(() => {
    const overdue = items.filter((i) => i.status === "overdue");
    const open = items.filter((i) => i.status === "open");
    const done = items.filter((i) => i.status === "done");
    return { overdue, open, done };
  }, [items]);

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <header className="mb-6">
        <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Scaling Up</div>
        <h1 className="text-2xl lg:text-3xl font-extrabold flex items-center gap-2">
          <ListChecks className="w-7 h-7" /> Action Items
        </h1>
      </header>

      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId(null);
            }}
          >
            <option value="">Holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(e.target.value || null)}
            disabled={!holdingId}
          >
            <option value="">Spółka…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Status | "all")}
          >
            <option value="all">Wszystkie statusy</option>
            <option value="open">open</option>
            <option value="overdue">overdue</option>
            <option value="done">done</option>
          </select>
        </CardContent>
      </Card>

      {itemsQuery.isLoading && (
        <div className="mb-6">
          <Skeleton className="h-5 w-36 mb-2" />
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-2.5 w-44 mt-1.5" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {companyId && grouped.overdue.length > 0 && (
        <Section title={`⚠️ Zaległe (${grouped.overdue.length})`}>
          {grouped.overdue.map((i) => (
            <ItemRow
              key={i.id}
              item={i}
              onComplete={() => updateMutation.mutate({ id: i.id, status: "done" })}
            />
          ))}
        </Section>
      )}

      {companyId && grouped.open.length > 0 && (
        <Section title={`Otwarte (${grouped.open.length})`}>
          {grouped.open.map((i) => (
            <ItemRow
              key={i.id}
              item={i}
              onComplete={() => updateMutation.mutate({ id: i.id, status: "done" })}
            />
          ))}
        </Section>
      )}

      {companyId && grouped.done.length > 0 && (
        <Section title={`Zamknięte (${grouped.done.length})`}>
          {grouped.done.map((i) => (
            <ItemRow key={i.id} item={i} />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h2 className="text-white/80 font-semibold mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ItemRow({ item, onComplete }: { item: ActionItem; onComplete?: () => void }) {
  return (
    <Card className="border-white/10 bg-white/[0.02]">
      <CardContent className="p-3 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm">{item.description}</div>
          <div className="text-[10px] text-white/40 mt-0.5">
            owner: {item.ownerUserId ? `${item.ownerUserId.slice(0, 8)}…` : "—"} · due:{" "}
            {item.dueDate ?? "—"}
          </div>
        </div>
        <Badge className={`border ${STATUS_CLASS[item.status]}`}>{item.status}</Badge>
        {onComplete && item.status !== "done" && (
          <Button size="sm" variant="outline" onClick={onComplete}>
            <Check className="w-3 h-3 mr-1" /> Done
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function ScalingUpActionItemsPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpActionItemsInner />
    </AdminGuard>
  );
}
